// Form and layout primitives used across the pages.

import {
  useEffect, useId, useRef, type ChangeEvent, type InputHTMLAttributes,
  type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { Icon, type IconName } from './Icon';
import { useT } from '../lib/i18n';

// ── field wrapper ───────────────────────────────────────────────────────────
interface FieldProps {
  label: string;
  error?: string;
  hint?: string;
  optional?: boolean;
  className?: string;
  children: (props: { id: string; invalid: boolean }) => ReactNode;
}

export function Field({ label, error, hint, optional, className, children }: FieldProps) {
  const id = useId();
  const t = useT();
  return (
    <div className={`field${className ? ` ${className}` : ''}`}>
      <label className="field__label" htmlFor={id}>
        {label}{optional && <span> · {t('common.optional')}</span>}
      </label>
      {children({ id, invalid: Boolean(error) })}
      {error ? <div className="field__error">{error}</div>
        : hint ? <div className="field__hint">{hint}</div> : null}
    </div>
  );
}

// ── inputs ──────────────────────────────────────────────────────────────────
type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
  label: string;
  error?: string;
  hint?: string;
  optional?: boolean;
  numeric?: boolean;
  wrapperClassName?: string;
};

export function TextField({ label, error, hint, optional, numeric, wrapperClassName, ...rest }: TextFieldProps) {
  return (
    <Field label={label} error={error} hint={hint} optional={optional} className={wrapperClassName}>
      {({ id, invalid }) => (
        <input
          {...rest}
          id={id}
          className={`input${numeric ? ' input--numeric' : ''}${invalid ? ' input--error' : ''}`}
          aria-invalid={invalid || undefined}
        />
      )}
    </Field>
  );
}

type SelectFieldProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> & {
  label: string;
  error?: string;
  hint?: string;
  wrapperClassName?: string;
};

export function SelectField({ label, error, hint, wrapperClassName, children, ...rest }: SelectFieldProps) {
  return (
    <Field label={label} error={error} hint={hint} className={wrapperClassName}>
      {({ id, invalid }) => (
        <select {...rest} id={id} className={`select${invalid ? ' select--error' : ''}`}
          aria-invalid={invalid || undefined}>
          {children}
        </select>
      )}
    </Field>
  );
}

type TextAreaFieldProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & {
  label: string;
  error?: string;
  hint?: string;
  optional?: boolean;
  wrapperClassName?: string;
};

export function TextAreaField({ label, error, hint, optional, wrapperClassName, ...rest }: TextAreaFieldProps) {
  return (
    <Field label={label} error={error} hint={hint} optional={optional} className={wrapperClassName}>
      {({ id, invalid }) => (
        <textarea {...rest} id={id} className="textarea" aria-invalid={invalid || undefined} />
      )}
    </Field>
  );
}

export function Checkbox({ label, checked, onChange }: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="checkbox">
      <input type="checkbox" checked={checked}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

// ── search box ──────────────────────────────────────────────────────────────
export function SearchInput({ value, onChange, placeholder }: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const t = useT();
  return (
    <div className="search">
      <Icon name="search" className="search__icon" />
      <input
        type="search"
        className="input"
        value={value}
        placeholder={placeholder}
        aria-label={t('common.search')}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

// ── modal ───────────────────────────────────────────────────────────────────
export function Modal({ title, onClose, footer, wide, children }: {
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  const t = useT();
  const panel = useRef<HTMLDivElement>(null);

  // Escape closes, and the panel takes focus so the dialog is reachable by
  // keyboard immediately. Background scrolling is locked while it is open.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal${wide ? ' modal--wide' : ''}`} role="dialog" aria-modal="true"
        aria-label={title} tabIndex={-1} ref={panel}>
        <div className="modal__head">
          <h2 className="modal__title">{title}</h2>
          <button type="button" className="btn btn--ghost btn--icon" onClick={onClose}
            aria-label={t('common.close')}>
            <Icon name="x" />
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </div>
  );
}

/** Confirmation prompt, used before anything destructive. */
export function ConfirmDialog({ title, message, confirmLabel, danger, busy, onConfirm, onCancel }: {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button type="button" className={`btn ${danger ? 'btn--danger' : 'btn--primary'}`}
            onClick={onConfirm} disabled={busy}>
            {busy && <span className="spinner" />}
            {confirmLabel}
          </button>
        </>
      }
    >
      <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
    </Modal>
  );
}

// ── states ──────────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, hint, action }: {
  icon: IconName;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <Icon name={icon} size={30} className="empty__icon" />
      <div className="empty__title">{title}</div>
      {hint && <div className="empty__hint">{hint}</div>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

export function LoadingRow({ label }: { label?: string }) {
  const t = useT();
  return (
    <div className="loading-row">
      <span className="spinner" />
      <span>{label ?? t('common.loading')}</span>
    </div>
  );
}

export function ErrorAlert({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const t = useT();
  return (
    <div className="alert alert--error">
      <Icon name="alert-circle" />
      <div style={{ flex: 1 }}>{message}</div>
      {onRetry && (
        <button type="button" className="btn btn--sm" onClick={onRetry}>{t('common.retry')}</button>
      )}
    </div>
  );
}

// ── pagination ──────────────────────────────────────────────────────────────
export function Pagination({ page, totalPages, total, perPage, onChange }: {
  page: number;
  totalPages: number;
  total: number;
  perPage: number;
  onChange: (page: number) => void;
}) {
  const t = useT();
  if (total === 0) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(total, page * perPage);
  return (
    <>
      <div className="pagination__info">{t('common.showing', { from, to, total })}</div>
      <div className="pagination">
        <button type="button" className="btn btn--sm" disabled={page <= 1}
          onClick={() => onChange(page - 1)}>
          <Icon name="chevron-left" size={14} />
          <span className="nowrap">{t('common.previous')}</span>
        </button>
        <span className="pagination__info nowrap">{t('common.page', { page, total: totalPages })}</span>
        <button type="button" className="btn btn--sm" disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}>
          <span className="nowrap">{t('common.next')}</span>
          <Icon name="chevron-right" size={14} />
        </button>
      </div>
    </>
  );
}

// ── badges ──────────────────────────────────────────────────────────────────
export function StatusBadge({ status }: { status: 'paid' | 'pending' }) {
  const t = useT();
  return (
    <span className={`badge badge--${status}`}>
      <span className="badge__dot" />
      {t(status === 'paid' ? 'status.paid' : 'status.pending')}
    </span>
  );
}

export function ActiveBadge({ active }: { active: boolean }) {
  const t = useT();
  return (
    <span className={`badge ${active ? 'badge--paid' : 'badge--neutral'}`}>
      {t(active ? 'common.active' : 'common.inactive')}
    </span>
  );
}
